import { Controller } from 'stimulus'

// Chunked diligent forms export.
// Asks the server for one signed Filestack zip URL per chunk of matching records, then lets
// the admin download the parts one by one, tracking which ones are already done.
export default class extends Controller {
  static targets = [
    'openButton',
    'openButtonLabel',
    'emptyAlert',
    'modal',
    'dialog',
    'summary',
    'warning',
    'error',
    'chunkList',
    'progress',
  ]

  static values = { url: String }

  connect() {
    this.downloadedParts = new Set()
    this.chunks = []
    this.boundKeydown = this.handleKeydown.bind(this)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundKeydown)
  }

  async open() {
    this.hideEmptyAlert()
    this.setLoading(true)

    try {
      const payload = await this.fetchChunks()
      if (payload.total_count === 0) {
        this.showEmptyAlert()
        return
      }
      this.chunks = payload.chunks || []
      this.downloadedParts = new Set()
      this.renderSummary(payload)
      this.renderChunks()
      this.renderProgress()
      this.hideError()
      this.showModal()
    } catch (error) {
      this.chunks = []
      this.renderSummary(null)
      this.chunkListTarget.innerHTML = ''
      this.progressTarget.textContent = ''
      this.showError(error.message)
      this.showModal()
    } finally {
      this.setLoading(false)
    }
  }

  close() {
    this.modalTarget.classList.add('hidden')
    document.removeEventListener('keydown', this.boundKeydown)
    if (this.hasOpenButtonTarget) this.openButtonTarget.focus()
  }

  closeFromBackdrop(event) {
    if (event.target === event.currentTarget) this.close()
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.close()
  }

  download(event) {
    const part = Number(event.currentTarget.dataset.part)
    this.downloadedParts.add(part)
    // Re-render after the browser has started the download, so replacing the clicked link
    // cannot cancel the navigation.
    setTimeout(() => {
      this.renderChunks()
      this.renderProgress()
    }, 0)
  }

  async fetchChunks() {
    const url = new URL(this.urlValue, window.location.origin)
    const currentFilters = new URLSearchParams(window.location.search)
    currentFilters.delete('page')
    currentFilters.forEach((value, key) => url.searchParams.set(key, value))

    let response
    try {
      response = await fetch(url.toString(), { headers: { Accept: 'application/json' } })
    } catch (_e) {
      throw new Error('Could not reach the server. Please check your connection and try again.')
    }

    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(payload.error || 'Unable to prepare the export. Please try again.')
    }
    return payload
  }

  showModal() {
    this.modalTarget.classList.remove('hidden')
    document.addEventListener('keydown', this.boundKeydown)
    if (this.hasDialogTarget) this.dialogTarget.focus()
  }

  setLoading(loading) {
    if (this.hasOpenButtonTarget) this.openButtonTarget.disabled = loading
    if (this.hasOpenButtonLabelTarget) {
      this.openButtonLabelTarget.textContent = loading ? 'Preparing export...' : 'Export'
    }
  }

  showEmptyAlert() {
    this.emptyAlertTarget.classList.remove('hidden')
  }

  hideEmptyAlert() {
    this.emptyAlertTarget.classList.add('hidden')
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  hideError() {
    this.errorTarget.classList.add('hidden')
    this.errorTarget.textContent = ''
  }

  renderSummary(payload) {
    if (!payload) {
      this.summaryTarget.textContent = ''
      this.warningTarget.classList.add('hidden')
      return
    }

    const parts = this.chunks.length
    this.summaryTarget.innerHTML = `
      <p><span class="font-medium">${payload.total_count}</span> matching
      ${this.pluralize(payload.total_count, 'record')} for the current filters, split into
      <span class="font-medium">${parts}</span> ${this.pluralize(parts, 'part')}.</p>
      <p class="mt-1 text-gray-500">Each part is a ZIP of up to ${payload.chunk_size} files, built by our file
      storage provider and downloaded directly from it.</p>`

    if (payload.large_export) {
      this.warningTarget.textContent =
        `This export covers ${payload.total_count} records. Downloading every part may take a while.`
      this.warningTarget.classList.remove('hidden')
    } else {
      this.warningTarget.classList.add('hidden')
    }
  }

  renderChunks() {
    this.chunkListTarget.innerHTML = this.chunks.map((chunk) => this.chunkRow(chunk)).join('')
  }

  chunkRow(chunk) {
    const downloaded = this.downloadedParts.has(chunk.part)

    return `
      <tr>
        <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900">
          Part ${chunk.part} of ${chunk.total_parts}
        </td>
        <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
          ${this.escapeHtml(chunk.range_label)} (${chunk.count} ${this.pluralize(chunk.count, 'file')})
        </td>
        <td class="whitespace-nowrap px-3 py-3 text-sm">${this.statusBadge(chunk, downloaded)}</td>
        <td class="whitespace-nowrap px-3 py-3 text-right text-sm">${this.actionCell(chunk, downloaded)}</td>
      </tr>`
  }

  statusBadge(chunk, downloaded) {
    if (chunk.error) {
      return '<span class="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">Failed</span>'
    }
    if (downloaded) {
      return '<span class="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Downloaded</span>'
    }
    return '<span class="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">Ready</span>'
  }

  actionCell(chunk, downloaded) {
    if (chunk.error) {
      return `<span class="text-xs text-red-700">${this.escapeHtml(chunk.error)}</span>`
    }

    return `
      <a href="${this.escapeAttr(chunk.url)}"
         target="_blank"
         rel="noopener"
         data-turbo="false"
         data-part="${chunk.part}"
         data-action="diligent-forms-export#download"
         title="${this.escapeAttr(chunk.filename)}"
         class="text-sm font-medium text-indigo-600 hover:text-indigo-900">
        ${downloaded ? 'Download again' : 'Download'}
      </a>`
  }

  renderProgress() {
    const total = this.chunks.length
    const done = this.downloadedParts.size
    const remaining = total - done
    const remainingText = remaining === 0 ? 'all parts downloaded' : `${remaining} remaining`
    this.progressTarget.textContent =
      `${done} of ${total} ${this.pluralize(total, 'part')} downloaded — ${remainingText}`
  }

  pluralize(count, word) {
    return count === 1 ? word : `${word}s`
  }

  escapeHtml(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  escapeAttr(value) {
    return this.escapeHtml(value).replace(/'/g, '&#39;')
  }
}
